import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import { isArray, isEmpty, isEqual, isObject, omit } from 'lodash';
import { convertNumbersToStrings, removeEmptyValues } from '../../utils/helper';

export const FiltersContext = createContext({});
const pageParams = ['page', 'page_size'];

const Filters = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const [defaultClear, setDefaultClear] = useState({
    params: {},
    isClear: false,
  });

  const [filters, setFilters] = useState({
    query: qs.parse(window.location.search.slice(1)),
    post: {},
  })

  const [filtersParams, setFiltersParams] = useState([]);

  const isFirstRender = useRef(true);

  const onChangePost = useCallback((filters) => {
    setFilters((prevState) => ({
      ...prevState,
      post: {
        ...prevState.post,
        ...filters,
      },
    }))
  }, []);

  const onChangeQuery = useCallback((newFilters) => {
    setFilters((prevState) => {
      const newQuery = removeEmptyValues({
        ...prevState.query,
        ...newFilters,
      })
      return {
        ...prevState,
        query: newQuery,
      }
    })
  }, []);

  const onAddDefaultClear = useCallback((filters) => {
    setDefaultClear((prevState) => ({
      ...prevState,
      params: {
        ...prevState.params,
        ...filters,
      },
    }))
  }, []);

  const onDeleteFilter = useCallback((filtersParams) => {
    setFilters((prev) => {
      const validKeys = isArray(filtersParams) ? ['page', ...filtersParams].filter((key) => typeof key === 'string') : pageParams;

      const newQuery = { ...prev.query };
      const newPost = { ...prev.post };

      validKeys.forEach((key) => {
        delete newQuery[key];
        delete newPost[key];
      });

      return {
        ...prev,
        query: newQuery,
        post: newPost,
      };
    });
  }, [])

  const onClearFilters = (options) => {
    if (isObject(options?.withDeleteParams)) {
      const { withDeleteParams } = options;
      if (isArray(withDeleteParams)) {
        onDeleteFilter(withDeleteParams)
        setDefaultClear((prevState) => ({
          ...prevState,
          isClear: true,
        }))
      }
    } else {
      setFilters((prevFilters) => {
        const filterKeys = (obj) => Object.fromEntries(
          Object.entries(obj).filter(([key]) => !filtersParams.includes(key)),
        );
        const query = omit(prevFilters.query, ['page'])
        const post = omit(prevFilters.post, ['page'])

        return {
          query: filterKeys(query),
          post: filterKeys(post),
        };
      });
      setDefaultClear((prevState) => ({
        ...prevState,
        isClear: true,
      }))
    }
  }

  useEffect(() => {
    if (defaultClear.isClear) {
      setDefaultClear(() => ({
        params: {},
        isClear: false,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultClear.isClear]);

  const onChangeFilter = (newFilters) => {
    if (typeof newFilters !== 'object' || newFilters === null || Array.isArray(newFilters)) {
      throw new Error('Filters not object');
    }

    const allowedKeys = ['query', 'post'];
    const keys = Object.keys(newFilters);

    if (!keys.some((key) => allowedKeys.includes(key))) {
      throw new Error('Filter must contain query or post');
    }

    if (keys.some((key) => !allowedKeys.includes(key))) {
      throw new Error('Only query and post keys are allowed');
    }

    for (const key of keys) {
      const value = newFilters[key];

      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`The ${key} field must be an object`);
      }
    }

    setFilters((prev) => {
      const baseQuery = omit({ ...prev.query, ...newFilters?.query || {} }, ['page'])
      const basePost = omit({ ...prev.post, ...newFilters?.post || {} }, ['page'])

      return {
        query: removeEmptyValues({ ...baseQuery, ...newFilters?.query }),
        post: removeEmptyValues({ ...basePost, ...newFilters?.post }),
      };
    });
  }

  const onAddFiltersParams = useCallback((params) => {
    if (params == null) return;

    const paramsToUpdate = Array.isArray(params) ? params : [params];

    setFiltersParams((prev) => {
      const stateSet = new Set(prev);
      paramsToUpdate.forEach((p) => {
        stateSet.has(p) ? stateSet.delete(p) : stateSet.add(p);
      });
      return Array.from(stateSet);
    });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const newQueryFilters = qs.parse(window.location.search.slice(1));

    if (!isEqual(filters.query, newQueryFilters) && !filters.clear) {
      const searchObj = { ...filters.query };
      history.push({
        pathname: location.pathname,
        search: qs.stringify({ ...searchObj }, {
          skipNulls: true,
          filter: (prefix, value) => {
            if (typeof value === 'string') {
              const trimmed = value.trim();
              return trimmed === '' ? undefined : trimmed;
            }
            if (Array.isArray(value) && value.length === 0) return undefined;
            if (value instanceof Object && Object.keys(value).length === 0) return undefined;
            return value;
          },
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query]);

  /**
   * Держим актуальным query параметры
   */
  useEffect(() => {
    const newQueryFilters = qs.parse(window.location.search.slice(1));
    const oldQueryFilters = convertNumbersToStrings(filters.query);

    if (!isEqual(oldQueryFilters, newQueryFilters)) {
      setFilters((prevState) => ({
        ...prevState,
        query: newQueryFilters,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const contextFilters = useMemo(() => {
    const query = defaultClear.isClear ? { ...filters.query, ...defaultClear.params } : { ...filters.query };
    const post = defaultClear.isClear ? { ...filters.post, ...defaultClear.params } : { ...filters.post };

    return {
      filters: {
        query,
        post,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.post]);

  const isClear = useMemo(() => {
    const isQuery = filtersParams.some((key) => filters.query.hasOwnProperty(key));
    const isPost = filtersParams.some((key) => filters.query.hasOwnProperty(key));

    if (isQuery || isPost) {
      return true;
    }

    if (!isEmpty(defaultClear.params)) {
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.post, filtersParams, defaultClear])

  const contextValue = {
    onChangePost,
    onChangeQuery,
    onAddDefaultClear,
    onChangeFilter,
    onDeleteFilter,
    onClearFilters,
    onAddFiltersParams,

    isClear,
    filtersParams,
    ...contextFilters,
  }

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  )
}

Filters.defaultProps = {

};

Filters.propTypes = {
  children: PropTypes.node,
};

export default Filters;
